import React, { useCallback, useEffect, useRef } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { isComposerSubmitShortcut } from './composerSubmitShortcut.ts';
import {
	COMPOSER_ENTRY_BODY_FONT_SIZE,
	COMPOSER_ENTRY_BODY_FONT_WEIGHT,
	COMPOSER_ENTRY_BODY_LINE_HEIGHT,
	COMPOSER_ENTRY_BODY_MIN_H,
	COMPOSER_ENTRY_BODY_PADDING_BOTTOM,
	COMPOSER_ENTRY_BODY_PADDING_TOP,
	COMPOSER_ENTRY_DETAILS_PLACEHOLDER,
	COMPOSER_ENTRY_ROW_GAP,
	COMPOSER_ENTRY_TITLE_FONT_SIZE,
	COMPOSER_TEXTAREA_MAX_H,
	COMPOSER_ENTRY_TITLE_FONT_WEIGHT,
	COMPOSER_ENTRY_TITLE_LINE_HEIGHT,
	COMPOSER_ENTRY_TITLE_PADDING_TOP,
} from './composerConstants.ts';
import {
	clearEmptyEditable,
	insertPlainTextFromClipboard,
	normalizeSingleLineEditableText,
	setEditableText,
} from './contentEditableUtils.ts';

interface ComposerTaskEditorProps {
	title: string;
	description: string;
	onTitleChange: (value: string) => void;
	onDescriptionChange: (value: string) => void;
	disabled: boolean;
	shouldFocus: boolean;
}

const TASK_TITLE_PLACEHOLDER = 'Task title';

const ComposerTaskEditor: React.FC<ComposerTaskEditorProps> = (props) => {
	const { title, description, onTitleChange, onDescriptionChange, disabled, shouldFocus } = props;
	const titleRef = useRef<HTMLDivElement>(null);
	const detailsRef = useRef<HTMLDivElement>(null);

	const syncTitle = useCallback(() => {
		const element = titleRef.current;
		if (!element) return;
		const nextTitle = normalizeSingleLineEditableText(element.innerText);
		if (element.innerText !== nextTitle) element.innerText = nextTitle;
		onTitleChange(nextTitle);
		clearEmptyEditable(element);
	}, [onTitleChange]);

	const syncDetails = useCallback(() => {
		const element = detailsRef.current;
		if (!element) return;
		const nextDescription = element.innerText;
		onDescriptionChange(nextDescription === '\n' ? '' : nextDescription);
		clearEmptyEditable(element);
	}, [onDescriptionChange]);

	// Enter alone moves to the field below; the submit chord belongs to the composer around this,
	// so it has to pass through rather than be spent on the hop.
	const handleTitleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' || isComposerSubmitShortcut(event)) return;
		event.preventDefault();
		detailsRef.current?.focus();
	}, []);

	const handleTitlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLDivElement>) => {
			event.preventDefault();
			const text = event.clipboardData.getData('text/plain').replace(/\r\n/g, '\n');
			const [nextTitle = '', ...detailLines] = text.split('\n');
			document.execCommand('insertText', false, nextTitle);
			onTitleChange(normalizeSingleLineEditableText(titleRef.current?.innerText ?? ''));
			if (detailLines.length > 0) {
				const pastedDetails = detailLines.join('\n');
				const nextDescription = [description, pastedDetails].filter(Boolean).join(description ? '\n' : '');
				onDescriptionChange(nextDescription);
				requestAnimationFrame(() => setEditableText(detailsRef.current, nextDescription));
			}
		},
		[description, onDescriptionChange, onTitleChange],
	);

	useEffect(() => {
		setEditableText(titleRef.current, title);
	}, [title]);

	useEffect(() => {
		setEditableText(detailsRef.current, description);
	}, [description]);

	useEffect(() => {
		if (shouldFocus) titleRef.current?.focus();
	}, [shouldFocus]);

	return (
		<VStack align="stretch" gap={COMPOSER_ENTRY_ROW_GAP} pt={2} minH="48px">
			<Box
				ref={titleRef}
				contentEditable={!disabled}
				suppressContentEditableWarning
				role="textbox"
				aria-multiline="false"
				aria-label="Task title"
				data-placeholder={TASK_TITLE_PLACEHOLDER}
				onInput={syncTitle}
				onPaste={handleTitlePaste}
				onKeyDown={handleTitleKeyDown}
				fontSize={COMPOSER_ENTRY_TITLE_FONT_SIZE}
				fontWeight={COMPOSER_ENTRY_TITLE_FONT_WEIGHT}
				color="fg"
				fontFamily="body"
				lineHeight={COMPOSER_ENTRY_TITLE_LINE_HEIGHT}
				pt={COMPOSER_ENTRY_TITLE_PADDING_TOP}
				outline="none"
				wordBreak="break-word"
				whiteSpace="pre-wrap"
				opacity={disabled ? 0.6 : 1}
				cursor={disabled ? 'default' : 'text'}
				css={{
					'&:empty::before': {
						content: 'attr(data-placeholder)',
						color: 'fg.subtle',
						pointerEvents: 'none',
					},
				}}
			/>
			<Box
				ref={detailsRef}
				contentEditable={!disabled}
				suppressContentEditableWarning
				role="textbox"
				aria-multiline="true"
				aria-label="Task details"
				data-placeholder={COMPOSER_ENTRY_DETAILS_PLACEHOLDER}
				onInput={syncDetails}
				onPaste={insertPlainTextFromClipboard}
				// Capped inline on desktop so a long note does not take the bar to the viewport's height;
				// uncapped in the mobile sheet, whose body is the one scroller.
				maxH={{ base: 'none', md: `${COMPOSER_TEXTAREA_MAX_H}px` }}
				overflowY={{ base: 'visible', md: 'auto' }}
				fontSize={COMPOSER_ENTRY_BODY_FONT_SIZE}
				fontWeight={COMPOSER_ENTRY_BODY_FONT_WEIGHT}
				color="fg"
				fontFamily="body"
				lineHeight={COMPOSER_ENTRY_BODY_LINE_HEIGHT}
				minH={{ md: COMPOSER_ENTRY_BODY_MIN_H }}
				pt={COMPOSER_ENTRY_BODY_PADDING_TOP}
				pb={COMPOSER_ENTRY_BODY_PADDING_BOTTOM}
				outline="none"
				wordBreak="break-word"
				whiteSpace="pre-wrap"
				opacity={disabled ? 0.6 : 1}
				cursor={disabled ? 'default' : 'text'}
				css={{
					'&:empty::before': {
						content: 'attr(data-placeholder)',
						color: 'fg.subtle',
						pointerEvents: 'none',
					},
				}}
			/>
		</VStack>
	);
};

export default ComposerTaskEditor;
